import { AIIntent } from './types';

export interface IntentExecutionResult {
  shouldSpeak: boolean;
  speechText?: string;
  action?: 'navigate' | 'open_newsfeed' | 'open_inbox_tab' | 'open_connectieflow' | 'open_agenda_networking' | 'open_agenda_new_item' | 'open_agenda_availability' | 'show_updates' | 'connectieflow_call' | 'connectieflow_schedule' | 'connectieflow_pause' | 'none';
  actionData?: any;
}

const ALLOWED_NAVIGATION_TARGETS = [
  'dashboard',
  'connecties',
  'agenda',
  'inbox',
  'mijn-verhaal',
  'vibes'
] as const;

type NavigationTarget = typeof ALLOWED_NAVIGATION_TARGETS[number];

function isValidNavigationTarget(target: string): target is NavigationTarget {
  return ALLOWED_NAVIGATION_TARGETS.includes(target as NavigationTarget);
}

export function handleAIIntent(intent: AIIntent): IntentExecutionResult {
  switch (intent.type) {
    case 'respond':
      return {
        shouldSpeak: true,
        speechText: intent.message,
        action: 'none'
      };

    case 'navigate':
      const target = intent.target.toLowerCase();
      
      if (!isValidNavigationTarget(target)) {
        console.warn('⚠️ Invalid navigation target:', target);
        return {
          shouldSpeak: true,
          speechText: `Sorry, ik kan niet naar ${intent.target} navigeren.`,
          action: 'none'
        };
      }

      console.log('🧭 Navigating to:', target);
      return {
        shouldSpeak: false,
        action: 'navigate',
        actionData: { target }
      };

    case 'explain':
      return {
        shouldSpeak: true,
        speechText: `Uitleg over ${intent.topic} komt binnenkort beschikbaar.`,
        action: 'none'
      };

    case 'ask_clarify':
      return {
        shouldSpeak: true,
        speechText: intent.question,
        action: 'none'
      };

    case 'open_newsfeed':
      return {
        shouldSpeak: false,
        action: 'open_newsfeed'
      };

    case 'open_inbox_tab':
      return {
        shouldSpeak: false,
        action: 'open_inbox_tab',
        actionData: { tab: intent.tab }
      };

    case 'open_connectieflow':
      return {
        shouldSpeak: false,
        action: 'open_connectieflow'
      };

    case 'open_agenda_networking':
      return {
        shouldSpeak: false,
        action: 'open_agenda_networking'
      };

    case 'open_agenda_new_item':
      return {
        shouldSpeak: false,
        action: 'open_agenda_new_item'
      };

    case 'schedule_availability':
      // Direct modal openen met prefill data, geen spraak confirmatie
      console.log('📅 Schedule availability:', intent.payload);
      return {
        shouldSpeak: false,
        action: 'open_agenda_availability',
        actionData: intent.payload
      };

    case 'show_updates':
      return {
        shouldSpeak: true,
        speechText: '', // Will be filled with actual updates
        action: 'show_updates'
      };
    
    case 'connectieflow_call':
      console.log('📞 Starting call in ConnectieFlow');
      return {
        shouldSpeak: false,
        action: 'connectieflow_call',
        actionData: intent.connectionId ? { connectionId: intent.connectionId } : undefined
      };
    
    case 'connectieflow_schedule':
      console.log('📅 Opening schedule in ConnectieFlow');
      const schedData = (intent as any).actionData;
      return {
        shouldSpeak: false,
        action: 'connectieflow_schedule',
        actionData: schedData ? schedData : (intent.connectionId ? { connectionId: intent.connectionId } : undefined)
      };
    
    case 'connectieflow_pause':
      console.log('⏸️ Pausing ConnectieFlow');
      return {
        shouldSpeak: false,
        action: 'connectieflow_pause',
        actionData: intent.connectionId ? { connectionId: intent.connectionId } : undefined
      };

    case 'idle':
      return {
        shouldSpeak: false,
        action: 'none'
      };

    default:
      console.warn('⚠️ Unknown intent type, fallback to idle');
      return {
        shouldSpeak: false,
        action: 'none'
      };
  }
}

